import { useState } from 'react';
import { CheckCircle2, Clock, AlertTriangle, Check, Play, Hourglass } from 'lucide-react';
import { toMin, fmtTimer, fmtDuracao, TOLERANCIA_CHAMADA_MIN } from './professorUtils';
import { ChamadaForaHorarioModal } from './ChamadaForaHorarioModal';
import { toast } from 'sonner';

/**
 * Timeline Smart Collapse — renderiza aulas e intervalos com collapse inteligente
 */
export function TimelineAulas({ aulas, statusMap, horarioAtual, proximaAula, agoraMin, chamadasDia, navigate, showAll = false }: {
  aulas: any[]; statusMap: Record<string, string>; horarioAtual: any; proximaAula: any;
  agoraMin: number; chamadasDia: any[]; navigate: (p: string, state?: any) => void; showAll?: boolean;
}) {
  const [showPast, setShowPast] = useState(false);
  const [showFuture, setShowFuture] = useState(false);
  const [selectedForaHorario, setSelectedForaHorario] = useState<any | null>(null);
  const isMobileView = typeof window !== 'undefined' && window.innerWidth < 768;

  // Contagem regressiva alinhada a agoraMin (sim ou civil) — sem wall clock próprio.
  const agoraSec = Math.max(0, agoraMin) * 60;

  // Classify each slot (aula ou intervalo)
  const classified = aulas.map((h, i) => {
    const isIntervalo = h.tipo === 'INTERVALO';
    const feita = !isIntervalo && statusMap[h.id] === 'CONCLUIDA';
    const atual = !isIntervalo && horarioAtual?.id === h.id;
    const passada = h.horaFim ? toMin(h.horaFim) < agoraMin : false;
    const futura = h.horaInicio ? toMin(h.horaInicio) > agoraMin : false;
    const minParaEsta = h.horaInicio ? toMin(h.horaInicio) - agoraMin : 999;
    const isProx = !isIntervalo && proximaAula?.id === h.id && minParaEsta <= 20;
    const isIntervaloAtivo = isIntervalo && !passada && !futura;

    const minAposFim = passada && h.horaFim ? agoraMin - toMin(h.horaFim) : 0;
    const pendente = !isIntervalo && passada && !feita && !atual && minAposFim <= TOLERANCIA_CHAMADA_MIN;
    const perdida  = !isIntervalo && passada && !feita && !atual && minAposFim >  TOLERANCIA_CHAMADA_MIN;

    const fimSec = h.horaFim ? toMin(h.horaFim) * 60 : 0;
    const toleranciaSec = TOLERANCIA_CHAMADA_MIN * 60;
    const expiraSec = fimSec + toleranciaSec;
    const countdownSec = pendente ? Math.max(0, expiraSec - agoraSec) : 0;

    return { h, i, feita, atual, passada, pendente, perdida, futura, isProx, isIntervalo, isIntervaloAtivo, countdownSec };
  });

  // Build visible list
  let visible: typeof classified;
  let hiddenPastCount = 0;
  let hiddenFutureCount = 0;

  if (showAll) {
    visible = [...classified];
  } else {
    const pendentes = classified.filter(c => c.pendente);
    const currentIdx = classified.findIndex(c => c.atual);
    const prevIdx = currentIdx > 0 ? currentIdx - 1 : -1;
    const nextIdx = currentIdx >= 0 && currentIdx < classified.length - 1 ? currentIdx + 1 : classified.findIndex(c => c.isProx);

    visible = [];
    if (prevIdx >= 0) visible.push(classified[prevIdx]);
    if (currentIdx >= 0) visible.push(classified[currentIdx]);
    if (nextIdx >= 0 && !visible.find(v => v.h.id === classified[nextIdx].h.id)) visible.push(classified[nextIdx]);

    if (currentIdx < 0) {
      const proxIdx = classified.findIndex(c => c.isProx || c.futura);
      if (proxIdx >= 0) {
        const visibleIds = new Set(visible.map(v => v.h.id));
        if (proxIdx > 0 && !visibleIds.has(classified[proxIdx - 1].h.id)) visible.push(classified[proxIdx - 1]);
        if (!visibleIds.has(classified[proxIdx].h.id)) visible.push(classified[proxIdx]);
        if (proxIdx < classified.length - 1 && !visibleIds.has(classified[proxIdx + 1].h.id)) visible.push(classified[proxIdx + 1]);
      } else if (classified.length > 0) {
        const start = Math.max(0, classified.length - 3);
        for (let i = start; i < classified.length; i++) visible.push(classified[i]);
      }
    }

    pendentes.forEach(p => { if (!visible.find(v => v.h.id === p.h.id)) visible.push(p); });

    const visibleIds = new Set(visible.map(v => v.h.id));
    hiddenPastCount = classified.filter(c => c.i < (prevIdx >= 0 ? prevIdx : currentIdx >= 0 ? currentIdx : 0) && !visibleIds.has(c.h.id)).length;
    hiddenFutureCount = classified.filter(c => c.i > (nextIdx >= 0 ? nextIdx : currentIdx >= 0 ? currentIdx : classified.length) && !visibleIds.has(c.h.id)).length;

    if (showPast) {
      classified.filter(c => !visibleIds.has(c.h.id) && c.i < (currentIdx >= 0 ? currentIdx : Infinity)).forEach(c => visible.push(c));
    }
    if (showFuture) {
      classified.filter(c => !visibleIds.has(c.h.id) && c.i > (currentIdx >= 0 ? currentIdx : -1)).forEach(c => visible.push(c));
    }
    visible.sort((a, b) => a.i - b.i);
  }

  const linkStyle: React.CSSProperties = {
    fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', padding: '4px 8px',
    display: 'flex', alignItems: 'center', gap: 4, borderRadius: 6,
    border: 'none', marginLeft: 28, marginBottom: 4, marginTop: 2,
    background: '#f1f5f9', color: '#64748b',
  };

  return (
    <div style={{ paddingLeft: 8 }}>
      {selectedForaHorario && (
        <ChamadaForaHorarioModal
          horario={selectedForaHorario}
          onClose={() => setSelectedForaHorario(null)}
          onConfirm={(justificativa) => {
            const h = selectedForaHorario;
            setSelectedForaHorario(null);
            navigate('/professor/chamada', {
              state: {
                foraDoHorario: true,
                horarioAulaId: h?.id,
                turmaId: h?.turmaId,
                materiaId: h?.materiaId,
                justificativaAtraso: justificativa,
              },
            });
          }}
        />
      )}

      {hiddenPastCount > 0 && !showPast && (
        <button style={linkStyle} onClick={() => setShowPast(true)}>
          <CheckCircle2 size={12} color="#16a34a" />
          Exibir {hiddenPastCount} anterior{hiddenPastCount > 1 ? 'es' : ''}
        </button>
      )}

      {visible.map((c, vi) => {
        const { h, feita, atual, pendente, perdida, futura, isProx, isIntervalo, isIntervaloAtivo, countdownSec } = c;
        const ch = chamadasDia.find((cd: any) => cd.horarioAulaId === h.id);
        const isLastVisible = vi === visible.length - 1 && !(hiddenFutureCount > 0 && !showFuture);
        const duracao = h.horaInicio && h.horaFim ? fmtDuracao(h.horaInicio, h.horaFim) : '';

        // ─── INTERVALO: render especial ───
        if (isIntervalo) {
          const passadoIntervalo = h.horaFim ? toMin(h.horaFim) < agoraMin : false;
          return (
            <div key={h.id} style={{ display: 'flex', gap: 10, alignItems: isMobileView ? 'flex-start' : 'center' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 20, flexShrink: 0 }}>
                <div style={{
                  width: 20, height: 20, borderRadius: '50%',
                  background: passadoIntervalo ? '#16a34a' : 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: isIntervaloAtivo ? '0 0 0 3px rgba(245,158,11,0.25)' : 'none',
                  border: passadoIntervalo ? 'none' : '1.5px solid #e5e7eb',
                  flexShrink: 0,
                }}>{passadoIntervalo ? <Check size={12} color="white" strokeWidth={3} /> : <Hourglass size={12} color="#92400e" />}</div>
                {!isLastVisible && <div style={{ width: 2, flex: 1, minHeight: 16, background: passadoIntervalo ? '#fde68a' : '#e5e7eb' }} />}
              </div>
              <div style={{
                flex: 1, padding: '6px 10px', borderRadius: 8, marginBottom: 2,
                background: isIntervaloAtivo ? '#fef3c7' : 'transparent',
                border: isIntervaloAtivo ? '1px solid #fde68a' : '1px solid transparent',
                opacity: passadoIntervalo ? 0.5 : 1,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#92400e', fontWeight: 600 }}>{h.horaInicio}</span>
                    <span style={{ fontWeight: 700, fontSize: '0.75rem', color: '#92400e', textTransform: 'uppercase' }}>
                      {h.descricao || 'Intervalo'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {isIntervaloAtivo && (
                      <span style={{ padding: '2px 7px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 700, background: '#f59e0b', color: 'white' }}>
                        Agora
                      </span>
                    )}
                    {duracao && (
                      <span style={{ fontSize: '0.75rem', color: '#b45309', fontFamily: 'monospace', fontWeight: 500 }}>{duracao}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        }

        // ─── AULA: render normal ──────────────────────
        let dotColor = '#d1d5db';
        let dotIcon = null as React.ReactNode;
        if (feita && atual) { dotColor = '#2563eb'; dotIcon = <Check size={14} color="white" strokeWidth={3} />; }
        else if (feita)   { dotColor = '#16a34a'; dotIcon = <Check size={14} color="white" strokeWidth={3} />; }
        else if (atual)   { dotColor = '#2563eb'; dotIcon = <Play size={12} color="white" fill="white" />; }
        else if (perdida) { dotColor = '#f59e0b'; dotIcon = <AlertTriangle size={13} color="white" />; }
        else if (pendente){ dotColor = '#d97706'; dotIcon = <AlertTriangle size={13} color="white" />; }
        else if (isProx)  { dotColor = '#d97706'; dotIcon = <Clock size={13} color="white" />; }

        const handleClick = () => {
          if (atual) navigate('/professor/chamada');
          else if (pendente) navigate('/professor/chamada');
          else if (perdida) setSelectedForaHorario(h);
          else if (isProx) {
            // Só permite navegar se faltam <= 5 min para a aula iniciar
            const minParaEsta = h.horaInicio ? toMin(h.horaInicio) - agoraMin : 999;
            if (minParaEsta <= 5) {
              navigate('/professor/chamada');
            } else {
              toast.info(`Aguarde. A aula inicia às ${h.horaInicio} (faltam ${minParaEsta} min).`);
            }
          }
        };
        const isClickable = atual || pendente || perdida || isProx;

        return (
          <div key={h.id} style={{ display: 'flex', gap: 10, alignItems: isMobileView ? 'flex-start' : 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 20, flexShrink: 0 }}>
              <div style={{
                width: 20, height: 20, borderRadius: '50%', background: dotColor,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: atual ? '0 0 0 3px rgba(37,99,235,0.25)' : pendente ? '0 0 0 3px rgba(217,119,6,0.2)' : 'none',
                flexShrink: 0,
              }}>{dotIcon}</div>
              {!isLastVisible && <div style={{ width: 2, flex: 1, minHeight: 16, background: feita ? '#bbf7d0' : '#e5e7eb' }} />}
            </div>

            <div style={{
              flex: 1, padding: '8px 10px', borderRadius: 8, marginBottom: 2,
              background: atual ? '#eff6ff' : pendente ? '#fffbeb' : 'transparent',
              border: atual ? '1px solid #bfdbfe' : pendente ? '1px solid #fde68a' : '1px solid transparent',
              opacity: atual || pendente ? 1 : (feita || perdida || futura) ? 0.75 : 1,
              cursor: isClickable ? 'pointer' : 'default', transition: 'background 0.2s',
            }} onClick={isClickable ? handleClick : undefined}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flex: 1 }}>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#6b7280', fontWeight: 600, flexShrink: 0 }}>{h.horaInicio}</span>
                    <span style={{ fontWeight: 700, fontSize: '0.75rem', color: 'var(--text-main, #1e293b)', textTransform: 'uppercase' }}>{h.materia?.nome || `${h.numeroAula}ª Aula`}</span>
                    {!isMobileView && h.turma?.nome && (
                      <>
                        <span style={{ fontSize: '0.75rem', color: '#cbd5e1' }}>—</span>
                        <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', flexShrink: 0 }}>
                          {h.turma.nome}
                        </span>
                      </>
                    )}
                    {h.coberturado && (
                      <span
                        style={{
                          padding: '1px 6px',
                          borderRadius: 4,
                          fontSize: '0.62rem',
                          fontWeight: 800,
                          background: '#dcfce7',
                          color: '#15803d',
                          textTransform: 'uppercase',
                          flexShrink: 0,
                        }}
                        title="Você é o substituto neste horário"
                      >
                        Cobertura
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    {feita && ch && (
                      <span style={{ fontSize: '0.75rem', color: '#16a34a', fontWeight: 600 }}>
                        {ch.totalPresentes ?? 0}P {ch.totalAusentes ?? 0}A {ch.totalAtrasados ?? 0}At
                      </span>
                    )}
                    {perdida && (
                      <span style={{
                        padding: '2px 7px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 700,
                        background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a', display: 'flex', alignItems: 'center', gap: 3,
                      }}>
                        <AlertTriangle size={11} color="#b45309" /> Fora do Horário
                      </span>
                    )}
                    {pendente && (
                      <span style={{
                        padding: '2px 7px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 700,
                        background: '#d97706', color: 'white', display: 'flex', alignItems: 'center', gap: 3,
                      }}>
                        <AlertTriangle size={11} color="white" />
                        Pendente · {fmtTimer(countdownSec)}
                      </span>
                    )}
                    {atual && !feita && (
                      <span style={{ padding: '2px 7px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 700, background: '#dbeafe', color: '#1e40af', textTransform: 'uppercase' }}>Agora</span>
                    )}
                    {isProx && (
                      <span style={{ padding: '2px 7px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 700, background: '#fef3c7', color: '#92400e' }}>
                        Próxima · {h.horaInicio ? toMin(h.horaInicio) - agoraMin : 0}min
                      </span>
                    )}
                    {duracao && (
                      <span style={{ fontSize: '0.75rem', color: atual ? '#1e293b' : '#94a3b8', fontFamily: 'monospace', fontWeight: atual ? 700 : 500 }}>
                        {duracao}
                      </span>
                    )}
                  </div>
                </div>
                {isMobileView && h.turma?.nome && (
                  <div style={{ paddingLeft: 42 }}>
                    <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
                      {h.turma.nome}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {hiddenFutureCount > 0 && !showFuture && (
        <button style={{ ...linkStyle, marginTop: 4 }} onClick={() => setShowFuture(true)}>
          <Clock size={12} color="#9ca3af" />
          Exibir {hiddenFutureCount} próxima{hiddenFutureCount > 1 ? 's' : ''}
        </button>
      )}
    </div>
  );
}
